import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { stockService } from '@/services/stockService';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Breadcrumbs, BackButton } from '@/components/shared';
import type { Stock } from '@/types/inventory.types';
import {
  AlertCircle,
  AlertTriangle,
  Search,
  Package,
  TrendingDown,
  Bell,
  CheckCircle,
} from 'lucide-react';
import { createLogger } from '@/lib/logger';

const log = createLogger('AlertsPage');

type AlertLevel = 'all' | 'critical' | 'warning';

interface StockWithTotal extends Stock {
  stockTotal?: number;
}

export function AlertsPage() {
  const { currentRestaurantId } = useAuth();
  const navigate = useNavigate();
  const [criticalStocks, setCriticalStocks] = useState<StockWithTotal[]>([]);
  const [warningStocks, setWarningStocks] = useState<StockWithTotal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState<AlertLevel>('all');

  // Charger les alertes
  useEffect(() => {
    const loadAlerts = async () => {
      if (!currentRestaurantId) return;

      setIsLoading(true);
      try {
        const [criticalResult, warningResult, allStocksResult] = await Promise.all([
          stockService.getCriticalStocks(currentRestaurantId),
          stockService.getWarningStocks(currentRestaurantId),
          stockService.getStocks(currentRestaurantId),
        ]);

        // Calculer le stock total par produit
        const stockTotalByProduct: Record<string, number> = {};
        if (allStocksResult.data) {
          allStocksResult.data.forEach((stock) => {
            stockTotalByProduct[stock.produit_id] =
              (stockTotalByProduct[stock.produit_id] || 0) + stock.quantite;
          });
        }

        // Ajouter le stock total à chaque alerte
        const enrichStock = (stock: Stock): StockWithTotal => ({
          ...stock,
          stockTotal: stockTotalByProduct[stock.produit_id] || stock.quantite,
        });

        setCriticalStocks((criticalResult.data || []).map(enrichStock));
        setWarningStocks((warningResult.data || []).map(enrichStock));
      } catch (error) {
        log.error('Erreur chargement alertes', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAlerts();
  }, [currentRestaurantId]);

  // Combiner et filtrer les alertes
  const filteredAlerts = useMemo(() => {
    let alerts: { stock: StockWithTotal; level: 'critical' | 'warning' }[] = [];

    if (filterLevel === 'all' || filterLevel === 'critical') {
      alerts = alerts.concat(
        criticalStocks.map((stock) => ({ stock, level: 'critical' as const }))
      );
    }

    if (filterLevel === 'all' || filterLevel === 'warning') {
      alerts = alerts.concat(
        warningStocks.map((stock) => ({ stock, level: 'warning' as const }))
      );
    }

    // Filtrer par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      alerts = alerts.filter(
        ({ stock }) =>
          stock.produits?.nom.toLowerCase().includes(query) ||
          stock.produits?.categorie.toLowerCase().includes(query)
      );
    }

    // Trier: critiques d'abord, puis par quantité restante
    return alerts.sort((a, b) => {
      if (a.level !== b.level) {
        return a.level === 'critical' ? -1 : 1;
      }
      return (a.stock.stockTotal || 0) - (b.stock.stockTotal || 0);
    });
  }, [criticalStocks, warningStocks, filterLevel, searchQuery]);

  const totalCritical = criticalStocks.length;
  const totalWarning = warningStocks.length;
  const totalAlerts = totalCritical + totalWarning;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des alertes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 sm:pb-6">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <BackButton />
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">Alertes de stock</h1>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {totalAlerts} alerte{totalAlerts > 1 ? 's' : ''} active{totalAlerts > 1 ? 's' : ''}
              </p>
            </div>
            <Bell
              size={22}
              className={totalCritical > 0 ? 'text-red-500 animate-pulse' : 'text-gray-400'}
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
        {/* Breadcrumbs */}
        <Breadcrumbs className="mb-4" />

        {/* Statistiques */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card padding="sm" className="bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800">
            <div className="flex flex-col sm:flex-row items-center sm:gap-3 text-center sm:text-left">
              <div className="p-2 bg-red-500 rounded-lg mb-1 sm:mb-0">
                <AlertCircle className="text-white" size={18} />
              </div>
              <div>
                <p className="text-xs text-red-600 dark:text-red-400">Critiques</p>
                <p className="text-xl sm:text-3xl font-bold text-red-700 dark:text-red-300">{totalCritical}</p>
              </div>
            </div>
          </Card>

          <Card padding="sm" className="bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800">
            <div className="flex flex-col sm:flex-row items-center sm:gap-3 text-center sm:text-left">
              <div className="p-2 bg-yellow-500 rounded-lg mb-1 sm:mb-0">
                <AlertTriangle className="text-white" size={18} />
              </div>
              <div>
                <p className="text-xs text-yellow-600 dark:text-yellow-400">Alertes</p>
                <p className="text-xl sm:text-3xl font-bold text-yellow-700 dark:text-yellow-300">{totalWarning}</p>
              </div>
            </div>
          </Card>

          <Card padding="sm" className="bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800">
            <div className="flex flex-col sm:flex-row items-center sm:gap-3 text-center sm:text-left">
              <div className="p-2 bg-green-500 rounded-lg mb-1 sm:mb-0">
                <CheckCircle className="text-white" size={18} />
              </div>
              <div>
                <p className="text-xs text-green-600 dark:text-green-400 hidden sm:block">Statut</p>
                <p className="text-xs sm:text-lg font-bold text-green-700 dark:text-green-300">
                  {totalAlerts === 0 ? 'OK' : 'Action'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filtres */}
        <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-row sm:gap-4 mb-4 sm:mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10"
            />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            <Button
              variant={filterLevel === 'all' ? 'primary' : 'outline'}
              onClick={() => setFilterLevel('all')}
              size="sm"
              className="whitespace-nowrap flex-shrink-0"
            >
              Toutes ({totalAlerts})
            </Button>
            <Button
              variant={filterLevel === 'critical' ? 'primary' : 'outline'}
              onClick={() => setFilterLevel('critical')}
              size="sm"
              className={`whitespace-nowrap flex-shrink-0 ${filterLevel === 'critical' ? 'bg-red-600 hover:bg-red-700' : ''}`}
            >
              Critiques ({totalCritical})
            </Button>
            <Button
              variant={filterLevel === 'warning' ? 'primary' : 'outline'}
              onClick={() => setFilterLevel('warning')}
              size="sm"
              className={`whitespace-nowrap flex-shrink-0 ${filterLevel === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}`}
            >
              Alertes ({totalWarning})
            </Button>
          </div>
        </div>

        {/* Liste des alertes */}
        {filteredAlerts.length === 0 ? (
          <Card padding="md" className="text-center">
            <CheckCircle size={40} className="mx-auto text-green-500 mb-3" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
              {totalAlerts === 0 ? 'Aucune alerte' : 'Aucun résultat'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {totalAlerts === 0
                ? 'Tous vos stocks sont à des niveaux acceptables.'
                : 'Aucune alerte ne correspond à votre recherche.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {filteredAlerts.map(({ stock, level }) => (
              <Card
                key={stock.id}
                padding="sm"
                className={`border-l-4 ${
                  level === 'critical'
                    ? 'border-l-red-500 bg-red-50 dark:bg-red-900/30'
                    : 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/30'
                }`}
              >
                <div className="flex items-start sm:items-center justify-between gap-3">
                  <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <div
                      className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                        level === 'critical' ? 'bg-red-100' : 'bg-yellow-100'
                      }`}
                    >
                      {level === 'critical' ? (
                        <AlertCircle size={18} className="text-red-600" />
                      ) : (
                        <AlertTriangle size={18} className="text-yellow-600" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
                          {stock.produits?.nom || 'Produit inconnu'}
                        </h3>
                        <Badge
                          variant={level === 'critical' ? 'danger' : 'warning'}
                          size="sm"
                        >
                          {level === 'critical' ? 'Critique' : 'Alerte'}
                        </Badge>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {stock.produits?.categorie} • {stock.zone}
                      </p>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center gap-1 justify-end">
                      <TrendingDown
                        size={14}
                        className={level === 'critical' ? 'text-red-500' : 'text-yellow-500'}
                      />
                      <span
                        className={`text-lg sm:text-2xl font-bold ${
                          level === 'critical' ? 'text-red-600' : 'text-yellow-600'
                        }`}
                      >
                        {stock.stockTotal?.toFixed(1) || stock.quantite.toFixed(1)}
                      </span>
                      <span className="text-gray-500 text-xs sm:text-sm">
                        {stock.produits?.unite}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 hidden sm:block">
                      Seuil: {level === 'critical'
                        ? stock.produits?.seuil_critique
                        : stock.produits?.seuil_alerte}{' '}
                      {stock.produits?.unite}
                    </p>
                  </div>
                </div>

                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/inventory')}
                    className="text-xs sm:text-sm"
                  >
                    <Package size={14} />
                    <span className="hidden sm:inline ml-1">Inventaire</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate('/inventory')}
                    className="text-xs sm:text-sm"
                  >
                    Réapprovisionner
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
